import {
  Banknote,
  CalendarClock,
  FileSignature,
  HandCoins,
  Landmark,
  Package,
  Receipt,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDate } from '@/lib/formatters';
import {
  addMonthsOnDay,
  computeMonthlyPaymentCents,
  isoToLocalDate,
  todayISO,
} from './loan-math';

// SBA rates routinely carry two decimal places (e.g. 9.25%) so we
// can't reuse the app-wide `formatPercent` helper which clamps to a
// single fractional digit. We render up to 3 decimals here and let
// Intl trim trailing zeros so 8% stays "8%" and 9.25% stays "9.25%".
const RATE_FMT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});
const formatLoanRate = (rate: number) => RATE_FMT.format(rate);

/**
 * Per-bucket allocation of the SBA loan proceeds. Each bucket has a
 * fixed budget that the operator can draw against during the draw
 * period; once spent, the bucket is "used up" and the unused balance
 * (if any) typically converts to principal once amortization begins.
 */
export type SBABucketKey =
  | 'working_capital'
  | 'inventory'
  | 'equipment'
  | 'closing_costs';

export interface SBABucket {
  key: SBABucketKey;
  label: string;
  /** Total amount the operator can draw against this bucket, in cents. */
  budgetCents: number;
  /** One-liner shown beneath the bucket title. */
  description?: string;
}

export interface SBADraw {
  /** Stable id (used as React key + future Supabase row id). */
  id: string;
  bucketKey: SBABucketKey;
  /** Cents drawn on `dateISO`. Always positive. */
  amountCents: number;
  /** ISO 'YYYY-MM-DD'. */
  dateISO: string;
  /** Operator-facing note (e.g. "Operating draw"). */
  note?: string;
  /**
   * `true` for funds the bank disbursed directly to a vendor at
   * closing (rather than cash to the operator). Surfaced as a small
   * "direct pay" hint in the draws table.
   */
  directPay?: boolean;
}

/**
 * A line item in the closing disclosure's prepaid finance charges
 * section (e.g. SBA packaging fee, SBA guarantee fee).
 */
export interface SBAClosingFee {
  id: string;
  label: string;
  amountCents: number;
}

/**
 * A line item in the closing disclosure's "paid to others on
 * borrower's behalf" section. Each row is a single check the lender
 * cut at closing to a third-party vendor or to the borrower's own
 * entity for reimbursement.
 */
export interface SBAClosingDisbursement {
  id: string;
  payee: string;
  amountCents: number;
  note?: string;
}

/**
 * Source-and-use breakdown from the lender's closing disclosure.
 * Total sources (note principal + borrower contribution) must equal
 * total uses (closing disbursements + prepaid fees + undisbursed
 * reserve). When provided, `SBALoanDraws` renders a "Closing summary"
 * card and verifies the reconciliation.
 */
export interface SBAClosingDisclosure {
  /** Cash the borrower brought to closing (equity injection). */
  borrowerContributionCents: number;
  /** SBA + bank fees deducted from loan proceeds at funding. */
  prepaidFeesCents: number;
  /** Breakdown of `prepaidFeesCents` for transparency. */
  prepaidFees: SBAClosingFee[];
  /** Cash paid to third parties on the borrower's behalf at closing. */
  closingDisbursementsCents: number;
  /** Breakdown of `closingDisbursementsCents` for transparency. */
  closingDisbursements: SBAClosingDisbursement[];
  /** Loan proceeds held back at closing for future draws. */
  undisbursedAtClosingCents: number;
}

export interface SBALoanDrawConfig {
  label: string;
  /** Bank's loan number (e.g. "72772-94008"). Surfaced near the title. */
  loanNumber?: string;
  lender?: string;
  /**
   * Face value of the SBA note in cents. Interest accrues against
   * this number; the buckets sum to the net post-fee allocation and
   * may differ from `facePrincipalCents` by fees and other
   * closing-related deductions.
   */
  facePrincipalCents: number;
  /** ISO 'YYYY-MM-DD' — the date the loan closed and funded. */
  closingDate: string;
  /**
   * Number of months between closing and the start of amortization.
   * Draws can be taken at any point in this window; after it ends
   * the unused balance converts to principal and the standard
   * amortization schedule begins.
   */
  drawPeriodMonths: number;
  /** Annual interest rate as a decimal (0.0925 = 9.25%). */
  annualRate: number;
  /**
   * Total amortization term in months (e.g. 120 for a 10-year loan).
   * This is the full repayment period; the post-draw-period P&I
   * schedule is computed against the actual drawn balance once the
   * draw window closes.
   */
  termMonths: number;
  /** The three SBA buckets, displayed in the order listed. */
  buckets: SBABucket[];
  /** Recorded draws across all buckets. Order is irrelevant. */
  draws: SBADraw[];
  /**
   * Source-and-use snapshot from the lender's closing disclosure.
   * Optional so legacy / partial configs still render; when present,
   * we render a dedicated "Closing summary" card.
   */
  closingDisclosure?: SBAClosingDisclosure;
}

const BUCKET_ICONS: Record<SBABucketKey, LucideIcon> = {
  working_capital: Banknote,
  inventory: Package,
  equipment: Wrench,
  closing_costs: Receipt,
};

interface BucketTotals {
  drawnCents: number;
  remainingCents: number;
  /** Pct 0..100, clamped so over-drawn buckets visibly cap at 100%. */
  percentDrawn: number;
  drawCount: number;
}

interface SBALoanDrawsProps {
  config: SBALoanDrawConfig;
}

export function SBALoanDraws({ config }: SBALoanDrawsProps) {
  const drawsByBucket = groupDrawsByBucket(config.draws);
  const totalsByBucket = new Map<SBABucketKey, BucketTotals>(
    config.buckets.map((b) => {
      const draws = drawsByBucket.get(b.key) ?? [];
      const drawnCents = draws.reduce((sum, d) => sum + d.amountCents, 0);
      const remainingCents = b.budgetCents - drawnCents;
      const percentDrawn =
        b.budgetCents > 0
          ? Math.min(100, Math.max(0, (drawnCents / b.budgetCents) * 100))
          : 0;
      return [
        b.key,
        { drawnCents, remainingCents, percentDrawn, drawCount: draws.length },
      ];
    }),
  );

  const totalBudgetCents = config.buckets.reduce(
    (sum, b) => sum + b.budgetCents,
    0,
  );
  const totalDrawnCents = config.draws.reduce(
    (sum, d) => sum + d.amountCents,
    0,
  );
  const totalRemainingCents = totalBudgetCents - totalDrawnCents;
  const drawPercent =
    totalBudgetCents > 0 ? (totalDrawnCents / totalBudgetCents) * 100 : 0;

  // Estimate the post-draw-period P&I assuming the operator pulls
  // the full net proceeds. Real SBA practice re-amortizes against
  // whatever was actually drawn at the close of the draw window;
  // until we know the final drawn balance this is the worst-case
  // payment the operator should plan around.
  const estimatedMonthlyPaymentCents = computeMonthlyPaymentCents(
    totalBudgetCents,
    config.annualRate,
    config.termMonths,
  );

  // During the draw period SBA charges interest only on the actual
  // drawn balance. One month of simple interest is a close-enough
  // approximation for the operator's cash-flow planning; the bank's
  // statement is the source of truth for the exact accrual.
  const currentMonthlyInterestCents = Math.round(
    (totalDrawnCents * config.annualRate) / 12,
  );

  // 6 months from closing, snapped to the same day-of-month. SBA
  // typically rolls partial months onto the next business day; for
  // operator-facing display we just show the calendar date.
  const drawPeriodEnd = addMonthsOnDay(
    config.closingDate,
    config.drawPeriodMonths,
    isoToLocalDate(config.closingDate).getDate(),
  );

  const today = todayISO();
  const drawPeriodOpen = today < drawPeriodEnd;
  const daysLeftInDrawPeriod = Math.max(
    0,
    Math.round(
      (isoToLocalDate(drawPeriodEnd).getTime() -
        isoToLocalDate(today).getTime()) /
        (1000 * 60 * 60 * 24),
    ),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={config.label}
        actions={
          <Badge variant={drawPeriodOpen ? 'info' : 'muted'}>
            {drawPeriodOpen
              ? `Draw period · ${daysLeftInDrawPeriod} day${daysLeftInDrawPeriod === 1 ? '' : 's'} left`
              : 'Draw period closed'}
          </Badge>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-old-growth" strokeWidth={1.6} />
            Loan overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-4">
            <Stat
              label="Face value"
              value={formatCurrency(config.facePrincipalCents)}
            />
            <Stat
              label="Bucket allocation"
              value={formatCurrency(totalBudgetCents)}
              hint={`Across ${config.buckets.length} SBA buckets`}
            />
            <Stat
              label="Closing date"
              value={formatDate(isoToLocalDate(config.closingDate))}
              hint={config.loanNumber ? `Loan #${config.loanNumber}` : undefined}
            />
            <Stat
              label="Draw period ends"
              value={formatDate(isoToLocalDate(drawPeriodEnd))}
              hint={`${config.drawPeriodMonths} months from closing`}
            />
            <Stat
              label="Total drawn"
              value={formatCurrency(totalDrawnCents)}
              hint={`${drawPercent.toFixed(1)}% of net proceeds`}
            />
            <Stat
              label="Available to draw"
              value={formatCurrency(totalRemainingCents)}
            />
            <Stat
              label="Interest rate"
              value={formatLoanRate(config.annualRate)}
              hint="Annual / APR"
            />
            <Stat
              label="Term"
              value={termLabel(config.termMonths)}
            />
            <Stat
              label="Monthly interest (current)"
              value={formatCurrency(currentMonthlyInterestCents)}
            />
            <Stat
              label="Est. monthly P&I"
              value={formatCurrency(estimatedMonthlyPaymentCents)}
              hint={`${formatLoanRate(config.annualRate)} on ${formatCurrency(totalBudgetCents)}`}
            />
            <Stat
              label="Draw count"
              value={String(config.draws.length)}
              hint={`Across ${config.buckets.length} buckets`}
            />
          </dl>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Total drawn</span>
              <span className="tabular-nums">
                {formatCurrency(totalDrawnCents)} of{' '}
                {formatCurrency(totalBudgetCents)}
              </span>
            </div>
            <ProgressBar percent={drawPercent} />
          </div>
        </CardContent>
      </Card>

      {config.closingDisclosure && (
        <ClosingSummaryCard
          disclosure={config.closingDisclosure}
          facePrincipalCents={config.facePrincipalCents}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {config.buckets.map((bucket) => (
          <BucketCard
            key={bucket.key}
            bucket={bucket}
            totals={totalsByBucket.get(bucket.key)!}
            draws={drawsByBucket.get(bucket.key) ?? []}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Renders the lender's closing disclosure as a side-by-side
 * sources & uses statement. We also verify that sources = uses and
 * surface a small "doesn't reconcile" warning if the operator types
 * a number off by a cent — better to fail loudly in the UI than to
 * silently misreport the book balance.
 */
function ClosingSummaryCard({
  disclosure,
  facePrincipalCents,
}: {
  disclosure: SBAClosingDisclosure;
  facePrincipalCents: number;
}) {
  const sourcesCents = facePrincipalCents + disclosure.borrowerContributionCents;
  const usesCents =
    disclosure.closingDisbursementsCents +
    disclosure.prepaidFeesCents +
    disclosure.undisbursedAtClosingCents;
  const reconciles = sourcesCents === usesCents;
  const reconciliationDeltaCents = sourcesCents - usesCents;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2">
            <FileSignature
              className="h-5 w-5 text-old-growth"
              strokeWidth={1.6}
            />
            Closing summary
          </CardTitle>
        </div>
        <Badge variant={reconciles ? 'success' : 'warning'}>
          {reconciles
            ? 'Reconciled'
            : `Off by ${formatCurrency(Math.abs(reconciliationDeltaCents))}`}
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 md:grid-cols-2">
          <ClosingColumn title="Sources of funds" totalCents={sourcesCents}>
            <ClosingRow
              label="Note principal"
              amountCents={facePrincipalCents}
            />
            <ClosingRow
              label="Borrower contribution"
              amountCents={disclosure.borrowerContributionCents}
              note="Non-loan funds contributed by borrower"
            />
          </ClosingColumn>

          <ClosingColumn title="Uses of funds" totalCents={usesCents}>
            <ClosingRow
              label="Paid to others on borrower's behalf"
              amountCents={disclosure.closingDisbursementsCents}
            />
            {disclosure.closingDisbursements.map((d) => (
              <ClosingRow
                key={d.id}
                label={d.payee}
                amountCents={d.amountCents}
                indent
                note={d.note}
              />
            ))}
            <ClosingRow
              label="Total financed prepaid finance charges"
              amountCents={disclosure.prepaidFeesCents}
            />
            {disclosure.prepaidFees.map((f) => (
              <ClosingRow
                key={f.id}
                label={f.label}
                amountCents={f.amountCents}
                indent
              />
            ))}
            <ClosingRow
              label="Undisbursed funds"
              amountCents={disclosure.undisbursedAtClosingCents}
              note="Held back at closing for future draws"
            />
          </ClosingColumn>
        </div>
      </CardContent>
    </Card>
  );
}

function ClosingColumn({
  title,
  totalCents,
  children,
}: {
  title: string;
  totalCents: number;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <ul className="space-y-1.5">{children}</ul>
      <div className="flex items-baseline justify-between border-t border-border pt-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Total
        </span>
        <span className="text-base font-medium tabular-nums tracking-headline text-foreground">
          {formatCurrency(totalCents)}
        </span>
      </div>
    </div>
  );
}

function ClosingRow({
  label,
  amountCents,
  indent,
  note,
}: {
  label: string;
  amountCents: number;
  indent?: boolean;
  note?: string;
}) {
  return (
    <li
      className={cn(
        'flex items-baseline justify-between gap-3 text-sm',
        indent && 'pl-4 text-xs text-muted-foreground',
      )}
    >
      <div className="flex min-w-0 flex-col">
        <span className={cn('truncate', indent ? '' : 'text-foreground')}>
          {label}
        </span>
        {note && !indent && (
          <span className="text-xs text-muted-foreground">{note}</span>
        )}
        {note && indent && (
          <span className="text-[11px] text-muted-foreground/80">{note}</span>
        )}
      </div>
      <span
        className={cn(
          'shrink-0 tabular-nums',
          indent ? 'text-xs text-muted-foreground' : 'font-medium text-foreground',
        )}
      >
        {formatCurrency(amountCents)}
      </span>
    </li>
  );
}

function BucketCard({
  bucket,
  totals,
  draws,
}: {
  bucket: SBABucket;
  totals: BucketTotals;
  draws: SBADraw[];
}) {
  const Icon = BUCKET_ICONS[bucket.key] ?? HandCoins;
  // Sort draws newest-first so the most recent activity sits at the
  // top of each bucket's ledger.
  const sortedDraws = [...draws].sort((a, b) =>
    a.dateISO === b.dateISO ? a.id.localeCompare(b.id) : a.dateISO < b.dateISO ? 1 : -1,
  );

  return (
    <Card className="flex flex-col">
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-old-growth" strokeWidth={1.6} />
            {bucket.label}
          </CardTitle>
          <Badge variant={totals.drawCount > 0 ? 'info' : 'muted'}>
            {totals.drawCount > 0
              ? `${totals.drawCount} draw${totals.drawCount === 1 ? '' : 's'}`
              : 'Untouched'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <dl className="grid grid-cols-3 gap-x-3 gap-y-2">
          <Stat
            label="Budget"
            value={formatCurrency(bucket.budgetCents)}
            size="sm"
          />
          <Stat
            label="Drawn"
            value={formatCurrency(totals.drawnCents)}
            size="sm"
          />
          <Stat
            label="Remaining"
            value={formatCurrency(totals.remainingCents)}
            size="sm"
          />
        </dl>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Drawn</span>
            <span className="tabular-nums">{totals.percentDrawn.toFixed(1)}%</span>
          </div>
          <ProgressBar percent={totals.percentDrawn} />
        </div>

        <div className="-mx-6 mt-2 border-t border-border">
          {sortedDraws.length === 0 ? (
            <div className="px-6 py-6 text-center text-xs text-muted-foreground">
              <CalendarClock className="mx-auto mb-2 h-4 w-4 text-muted-foreground/70" />
              No draws recorded yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="pr-6">Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedDraws.map((draw) => (
                  <TableRow key={draw.id}>
                    <TableCell className="pl-6 tabular-nums text-xs">
                      {formatDate(isoToLocalDate(draw.dateISO))}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {formatCurrency(draw.amountCents)}
                    </TableCell>
                    <TableCell className="pr-6 text-xs text-muted-foreground">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-foreground/80">
                          {draw.note ?? '—'}
                        </span>
                        {draw.directPay && (
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/80">
                            Lender direct-pay
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  hint,
  size = 'md',
}: {
  label: string;
  value: string;
  hint?: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          'font-medium tabular-nums tracking-headline text-foreground',
          size === 'md' ? 'text-lg' : 'text-sm',
        )}
      >
        {value}
      </dd>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
      <div
        className="h-full rounded-full bg-old-growth/70 transition-[width] duration-300"
        // A 2% floor keeps very small first draws visible — without
        // it a $50 draw on a $250k bucket would render as a zero-px
        // bar and look identical to "no draws yet".
        style={{ width: `${clamped === 0 ? 0 : Math.max(2, clamped)}%` }}
      />
    </div>
  );
}

function termLabel(months: number): string {
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} yr${years === 1 ? '' : 's'}`;
  }
  return `${months} mo`;
}

function groupDrawsByBucket(draws: SBADraw[]): Map<SBABucketKey, SBADraw[]> {
  const out = new Map<SBABucketKey, SBADraw[]>();
  for (const d of draws) {
    const arr = out.get(d.bucketKey);
    if (arr) {
      arr.push(d);
    } else {
      out.set(d.bucketKey, [d]);
    }
  }
  return out;
}
